/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { type DesktopSessionState, desktopSessionStateSchema } from '@poweralpha/common';
import { jsonCodec } from './storage';

/**
 * Local copy of the desktop session, written when the page is hidden while a server save is
 * still pending (saves are debounced), so a change made just before closing the tab is kept.
 * It exists only until the session is restored, so an idle tab closed later cannot bring back
 * stale windows over a newer session saved from another tab or device.
 */
const getJournalKey = (userId: string) => `pa-desktop-session:v1:${userId}`;

// Same schema the server validates saves with.
const journalCodec = jsonCodec(desktopSessionStateSchema);

/** The journaled session when it is newer than the server's, else the server's. */
export function readDesktopJournal(
  userId: string,
  serverState: DesktopSessionState,
): DesktopSessionState {
  try {
    const stored = localStorage.getItem(getJournalKey(userId));
    const journal = stored === null ? undefined : journalCodec.safeDecode(stored);
    if (
      journal?.success &&
      Date.parse(journal.data.updatedAt) > Date.parse(serverState.updatedAt)
    ) {
      return journal.data;
    }
  } catch {
    // Inaccessible storage: the server session still applies.
  }
  return serverState;
}

export function writeDesktopJournal(userId: string, state: DesktopSessionState) {
  try {
    localStorage.setItem(getJournalKey(userId), journalCodec.encode(state));
  } catch {
    // Storage unavailable or full: the server session still applies.
  }
}

export function removeDesktopJournal(userId: string) {
  try {
    localStorage.removeItem(getJournalKey(userId));
  } catch {
    // Storage unavailable: nothing was journaled.
  }
}
